import apiestoqueweb from '@/services/api-recebimentoweb';

import normalizaTexto from '@/utils/normaliza-texto';

function paramsFromWhere(where = {}) {
  const params = {};

  if (where.search) params.search = where.search;

  // O campo só acompanha o termo: sozinho ele não filtra nada, e mandá-lo à toa mudaria a
  // assinatura dos filtros (e a chave do cache) sem mudar o resultado.
  if (where.search && where.campo) params.campo = where.campo;

  if (where.status && where.status !== 'todos') params.status = where.status;
  if (where.marca) params.marca = where.marca;

  if (where.fornecedores?.length) params.fornecedores = where.fornecedores.join(',');
  if (where.grupos?.length) params.grupos = where.grupos.join(',');

  return params;
}

// Identidade do conjunto de filtros. Serve para saber se o total já contado continua valendo:
// o total só muda quando os filtros mudam, nunca ao virar de página.
function assinaturaDosFiltros(where) {
  return JSON.stringify(paramsFromWhere(where));
}

// Cache de páginas já buscadas, em memória (fora do state: é cache, não estado de tela, e
// não precisa de reatividade). Vale para reabrir o menu e para rolar de volta — nos dois
// casos a página já foi paga uma vez e refazer a ida até a EC2 só adiciona espera.
// O estoque pode ficar alguns minutos defasado, então o TTL curto é aceitável.
const TTL_PAGINA = 2 * 60 * 1000;
const LIMITE_DE_PAGINAS_EM_CACHE = 60;
const cacheDePaginas = new Map();

function chaveDaPagina(assinatura, { limit, offset, orderBy }) {
  return `${assinatura}|${limit}|${offset}|${orderBy}`;
}

function leDoCache(chave) {
  const registro = cacheDePaginas.get(chave);
  if (!registro) return null;

  if (Date.now() - registro.quando > TTL_PAGINA) {
    cacheDePaginas.delete(chave);
    return null;
  }

  return registro.resultado;
}

function gravaNoCache(chave, resultado) {
  // Descarta a entrada mais antiga quando estoura — Map preserva ordem de inserção.
  if (cacheDePaginas.size >= LIMITE_DE_PAGINAS_EM_CACHE) {
    cacheDePaginas.delete(cacheDePaginas.keys().next().value);
  }

  cacheDePaginas.set(chave, { quando: Date.now(), resultado });
}

// Ordem convencional dos tamanhos por letra. O que não estiver aqui cai no alfabético.
const TAMANHOS_POR_LETRA = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG', 'XXG'];

function numeroDoTamanho(tamanho) {
  const numero = Number(String(tamanho).trim().replace(',', '.'));
  return Number.isFinite(numero) ? numero : null;
}

// Ordena a grade como uma pessoa espera ler: 36, 38, 40… e PP, P, M, G, GG.
// Não dá para fazer isso no SQL — `json_agg(... order by protamanho)` compara texto, e aí
// "10" viria antes de "8".
function comparaTamanhos(a, b) {
  const numeroA = numeroDoTamanho(a);
  const numeroB = numeroDoTamanho(b);

  if (numeroA !== null && numeroB !== null) return numeroA - numeroB;

  // Grade numérica e grade por letra raramente convivem no mesmo produto; se conviverem,
  // os números vêm primeiro.
  if (numeroA !== null) return -1;
  if (numeroB !== null) return 1;

  const posicaoA = TAMANHOS_POR_LETRA.indexOf(normalizaTexto(a).toUpperCase());
  const posicaoB = TAMANHOS_POR_LETRA.indexOf(normalizaTexto(b).toUpperCase());

  if (posicaoA !== -1 && posicaoB !== -1) return posicaoA - posicaoB;
  if (posicaoA !== -1) return -1;
  if (posicaoB !== -1) return 1;

  return normalizaTexto(a).localeCompare(normalizaTexto(b));
}

// Valor monetário da API (numeric chega como string ou null). Ausente continua null, e não
// zero: produto sem preço sincronizado não pode aparecer como "R$ 0,00".
function valorOuNulo(valor) {
  if (valor === null || valor === undefined || valor === '') return null;

  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

// Menor e maior valor de uma lista, ignorando ausentes. Sem nenhum valor, os dois são null.
function faixaDeValores(valores) {
  const presentes = valores.filter((valor) => valor !== null);
  if (presentes.length === 0) return { minimo: null, maximo: null };

  return { minimo: Math.min(...presentes), maximo: Math.max(...presentes) };
}

// Produto pode ter SKU com cor cadastrada e SKU sem. Os dois casos precisam aparecer no
// detalhe do tamanho, então o vazio vira um rótulo próprio em vez de ser descartado.
export const SEM_COR = 'Sem cor definida';

function ordenaCores(porCor) {
  return [...porCor.values()]
    .map(({
      cor, preco, custos, qtde, condicional, eans,
    }) => ({
      cor,
      preco,
      custo: faixaDeValores(custos),
      qtde,
      condicional,
      // Cor, tamanho e preço identificam um SKU, então normalmente há um EAN só. Mais de um
      // é a mesma peça cadastrada duas vezes pelo mesmo preço: aí nenhum dos códigos
      // representa a linha sozinho, e exibir um deles seria pior que não exibir.
      ean: eans.size === 1 ? [...eans][0] : null,
    }))
    .sort((a, b) => {
      // "Sem cor definida" sempre por último: é ausência de dado, não uma cor.
      if (a.cor !== b.cor) {
        if (a.cor === SEM_COR) return 1;
        if (b.cor === SEM_COR) return -1;

        return normalizaTexto(a.cor).localeCompare(normalizaTexto(b.cor));
      }

      // Mesma cor em mais de um preço: do mais barato ao mais caro.
      return (a.preco ?? 0) - (b.preco ?? 0);
    });
}

// A grade vem da API com uma entrada por SKU (moeidproduto é unique), então o mesmo tamanho
// aparece repetido quando o produto tem mais de uma cor. Somamos por tamanho aqui — e não no
// SQL — porque assim a soma dos chips fecha exatamente com o `total` do card: são as mesmas
// linhas que a query agregou.
//
// A repartição por cor é guardada junto, e não recalculada depois: o detalhe do tamanho abre
// a partir dela, sem nova ida à API. Pela mesma construção, a soma das cores de um tamanho
// fecha com a quantidade do chip.
//
// A repartição é por cor E preço. No ERP o preço é da faixa de tamanho, mas há produtos com
// duas faixas cobrindo os mesmos tamanhos: a mesma cor e tamanho cadastrados duas vezes, cada
// um com seu preço e sua etiqueta (ver scripts/diagnostico-faixas-erp-sqlserver.sql na API).
// Somar essas peças numa linha só esconderia qual etiqueta tem qual preço.
function somaGradePorTamanho(grade) {
  const porTamanho = new Map();

  (grade ?? [])
    .filter((item) => item?.tamanho)
    .forEach((item) => {
      if (!porTamanho.has(item.tamanho)) {
        porTamanho.set(item.tamanho, {
          qtde: 0, condicional: 0, cores: new Map(), precos: [], custos: [],
        });
      }

      const entrada = porTamanho.get(item.tamanho);
      const qtde = Number(item.total ?? 0);
      const condicional = Number(item.condicional ?? 0);
      const cor = String(item.cor ?? '').trim() || SEM_COR;
      const ean = String(item.ean ?? '').trim();
      const preco = valorOuNulo(item.preco);
      const custo = valorOuNulo(item.custo);

      entrada.qtde += qtde;
      entrada.condicional += condicional;
      entrada.precos.push(preco);
      entrada.custos.push(custo);

      const chave = `${cor}|${preco ?? ''}`;
      if (!entrada.cores.has(chave)) {
        entrada.cores.set(chave, {
          cor, preco, custos: [], qtde: 0, condicional: 0, eans: new Set(),
        });
      }

      const porCor = entrada.cores.get(chave);
      porCor.qtde += qtde;
      porCor.condicional += condicional;
      porCor.custos.push(custo);
      if (ean) porCor.eans.add(ean);
    });

  // Normalmente o tamanho tem um preço e um custo só. Guardamos mínimo e máximo para que
  // faixas sobrepostas (ou preço por cor) apareçam como "mín–máx" no chip, e não como um
  // valor escolhido ao acaso entre as linhas.
  return [...porTamanho]
    .map(([tamanho, {
      qtde, condicional, cores, precos, custos,
    }]) => ({
      tamanho,
      qtde,
      condicional,
      cores: ordenaCores(cores),
      preco: faixaDeValores(precos),
      custo: faixaDeValores(custos),
    }))
    .sort((a, b) => comparaTamanhos(a.tamanho, b.tamanho));
}

// Agrupamento é feito por prodescricao (por enquanto), então cada tamanho/cor de um
// mesmo produto vira uma entrada na "grade" do card, e o nome vira o identificador do grupo.
function mapProduto(row) {
  return {
    id: row.nome,
    nome: row.nome,
    fornecedor: row.fornecedor,
    grupo: row.grupo,
    marca: row.marca,
    total: Number(row.total ?? 0),
    condicional: Number(row.condicional ?? 0),

    // A API só devolve `ean` quando a grade tem um SKU único — com vários, o código de
    // barras é de um tamanho/cor específico e não representa o card. Nesse caso resta a
    // contagem, e o EAN exato aparece por linha no detalhe de cores.
    ean: row.ean ?? null,
    eans: Number(row.eans ?? 0),

    // Referência do modelo no fabricante. A API só devolve quando a grade inteira concorda.
    numeroFabrica: row.numerofabrica ?? null,

    // Faixas do card inteiro, calculadas pela API sobre todas as linhas — inclusive as sem
    // tamanho, que não entram na grade. É o que dá preço ao produto sem grade.
    preco: { minimo: valorOuNulo(row.precominimo), maximo: valorOuNulo(row.precomaximo) },
    custo: { minimo: valorOuNulo(row.custominimo), maximo: valorOuNulo(row.customaximo) },

    grade: somaGradePorTamanho(row.grade),
  };
}

export default {
  namespaced: true,

  state: {
    // Total da listagem e a assinatura de filtros a que ele pertence. Ver a action `find`.
    totalPorFiltro: { assinatura: null, total: 0 },

    // Caches usados pelos filtros (fornecedor/grupo/marca): a tabela de movimentacaoestoque
    // tem ~30 mil linhas, então NÃO pode ser buscada inteira, mas fornecedor/grupo/marca são
    // listas pequenas — buscamos uma vez e filtramos no cliente (autocomplete), evitando
    // um request por letra digitada.
    fornecedores: null,
    grupos: null,
    marcas: null,
  },

  getters: {
    fornecedores: (state) => state.fornecedores ?? [],
    grupos: (state) => state.grupos ?? [],
    marcas: (state) => state.marcas ?? [],
  },

  // NUNCA ALTERAR state DIRETAMENTE EM ACTIONS
  actions: {
    // O /count refaz exatamente o mesmo agrupamento da listagem, então custa o mesmo que ela.
    // Como o total só muda quando os filtros mudam — virar de página não altera o total —, ele
    // é consultado uma vez por conjunto de filtros e reaproveitado nas páginas seguintes.
    // Isso corta metade das requisições de uma rolagem inteira.
    async find({ commit, state }, {
      where = {}, limit, offset, orderBy,
    } = {}) {
      const params = paramsFromWhere(where);
      const assinatura = assinaturaDosFiltros(where);

      const chave = chaveDaPagina(assinatura, { limit, offset, orderBy });
      const emCache = leDoCache(chave);

      if (emCache) {
        // O total vem junto no cache: registrar a assinatura evita que a página seguinte
        // desses mesmos filtros dispare um /count que já não é necessário.
        if (state.totalPorFiltro.assinatura !== assinatura) {
          commit('SET_TOTAL_POR_FILTRO', { assinatura, total: emCache.total });
        }

        return emCache;
      }

      const precisaContar = state.totalPorFiltro.assinatura !== assinatura;

      const [{ data }, total] = await Promise.all([
        apiestoqueweb.get('estoque/produtos', {
          params: {
            ...params, orderBy, limit, offset,
          },
        }),
        precisaContar
          ? apiestoqueweb
            .get('estoque/produtos/count', { params })
            .then(({ data: { count } }) => Number(count))
          : Promise.resolve(state.totalPorFiltro.total),
      ]);

      if (precisaContar) commit('SET_TOTAL_POR_FILTRO', { assinatura, total });

      const resultado = { data: data.map(mapProduto), total };
      gravaNoCache(chave, resultado);

      return resultado;
    },

    // Descarta o cache de páginas: use ao forçar atualização (ex.: puxar para recarregar),
    // quando o usuário quer o número do instante em vez do que está em memória.
    invalidarCache({ commit }) {
      cacheDePaginas.clear();
      commit('SET_TOTAL_POR_FILTRO', { assinatura: null, total: 0 });
    },

    // Busca a lista completa de fornecedores uma única vez e guarda em cache no state.
    // Use { force: true } para forçar uma nova consulta (ex.: botão de recarregar).
    async findFornecedoresComCache({ commit, state }, { force } = {}) {
      if (!force && state.fornecedores) return state.fornecedores;

      const { data } = await apiestoqueweb.get('fornecedor', {
        params: { orderBy: 'fornome' },
      });

      const lista = data.map((item) => ({ id: item.foridfornecedor, nome: item.fornome }));
      commit('SET_FORNECEDORES', lista);

      return lista;
    },

    // Busca a lista completa de grupos uma única vez e guarda em cache no state.
    async findGruposComCache({ commit, state }, { force } = {}) {
      if (!force && state.grupos) return state.grupos;

      const { data } = await apiestoqueweb.get('grupo', {
        params: { orderBy: 'grudescricao' },
      });

      const lista = data.map((item) => ({ id: item.gruidgrupo, nome: item.grudescricao }));
      commit('SET_GRUPOS', lista);

      return lista;
    },

    // Busca a lista completa de marcas uma única vez e guarda em cache no state.
    async findMarcasComCache({ commit, state }, { force } = {}) {
      if (!force && state.marcas) return state.marcas;

      const { data } = await apiestoqueweb.get('marca', {
        params: { orderBy: 'mardescricao' },
      });

      const lista = data.map((item) => ({ id: item.maridmarca, nome: item.mardescricao }));
      commit('SET_MARCAS', lista);

      return lista;
    },
  },

  // APENAS O MUTATION FAZ ALTERAÇÕES NO STATE
  mutations: {
    SET_TOTAL_POR_FILTRO(state, { assinatura, total }) {
      state.totalPorFiltro = { assinatura, total };
    },

    SET_FORNECEDORES(state, lista) {
      state.fornecedores = lista ?? [];
    },

    SET_GRUPOS(state, lista) {
      state.grupos = lista ?? [];
    },

    SET_MARCAS(state, lista) {
      state.marcas = lista ?? [];
    },
  },
};
